/**
 * Pure manufacturing logic (CC-6: NEVER use Lingui macros here).
 *
 * A bill of materials (bill_of_materials) defines the components
 * (bom_component: product × quantity) required to produce one recipe LOT
 * of the finished product (bom.qty units).
 *
 * A manufacturing order (manufacturing_order) produces `qty` units of the
 * finished product. To do so:
 *   1) SCALES each component: needed = component.qty × (orderQty / bom.qty).
 *   2) CONSUMES the components (outbound stock_move + negative AVCO layer at
 *      each component's current average cost).
 *   3) PRODUCES the finished product (inbound stock_move + positive AVCO layer) whose
 *      UNIT COST is derived from Σ(cost of consumed components) / orderQty.
 *
 * Quantities in INTEGERS (minor units, scale factor 1000), same as F1–F4.
 * UNIT COSTS are per whole unit (not per thousandth).
 */

import { QTY_SCALE } from "./units";
import { type AvcoState, averageUnitCost, valueOf } from "./valuation";

export type ManufacturingState = "draft" | "done";

export const MANUFACTURING_STATES: readonly ManufacturingState[] = ["draft", "done"] as const;

export function isManufacturingState(value: string): value is ManufacturingState {
  return (MANUFACTURING_STATES as readonly string[]).includes(value);
}

export function coerceManufacturingState(value: string | null | undefined): ManufacturingState {
  return value && isManufacturingState(value) ? value : "draft";
}

/** Can it be produced (draft→done)? Only from draft. */
export function canProduce(state: string | null | undefined): boolean {
  return coerceManufacturingState(state) === "draft";
}

/** BoM component (relevant subset for calculation). */
export interface BomComponentLike {
  product_id: string | null;
  /** Component quantity per recipe lot (minor units). */
  qty: number | null;
}

/**
 * Scales component quantity to order quantity.
 *   needed = componentQty × orderQty / bomQty
 * `bomQty` is the finished product quantity produced by a recipe lot (>0).
 * All quantities are scaled ×1000; the factor cancels out in the ratio
 * orderQty/bomQty, so it multiplies and then divides by bomQty.
 */
export function scaleComponentQty(componentQty: number, orderQty: number, bomQty: number): number {
  if (bomQty <= 0) {
    return 0;
  }
  return Math.round((componentQty * orderQty) / bomQty);
}

/** Planned consumption of a component for a manufacturing order. */
export interface ComponentConsumption {
  productId: string;
  /** Quantity to consume (minor units, > 0). */
  qty: number;
}

/**
 * Plans component consumption for an order: scales each BoM component
 * to the quantity to manufacture. Ignores components without product or with
 * qty <= 0. Deterministic, side-effect free.
 */
export function planComponentConsumption(
  components: BomComponentLike[],
  orderQty: number,
  bomQty: number,
): ComponentConsumption[] {
  const plan: ComponentConsumption[] = [];
  for (const c of components) {
    const productId = c.product_id;
    if (!productId) {
      continue;
    }
    const qty = scaleComponentQty(c.qty ?? 0, orderQty, bomQty);
    if (qty <= 0) {
      continue;
    }
    plan.push({ productId, qty });
  }
  return plan;
}

/** Outbound cost of a component upon consumption (current AVCO). */
export interface ConsumedComponentCost {
  productId: string;
  /** Consumed quantity (minor units). */
  qty: number;
  /** Current moving average cost per whole unit. */
  unitCost: number;
  /** Consumed value (minor units of currency). */
  value: number;
}

/**
 * Values component consumption at the CURRENT moving average cost of its
 * AVCO state. Returns unit cost and consumed (positive) value.
 */
export function valueComponentConsumption(
  state: AvcoState,
  qty: number,
): ConsumedComponentCost & { productId: string } {
  const unitCost = averageUnitCost(state);
  return {
    productId: "",
    qty,
    unitCost,
    value: valueOf(qty, unitCost),
  };
}

/**
 * Unit cost of the manufactured product: sum of the VALUE of all consumed
 * components divided by produced quantity (per whole unit).
 *
 *   producedUnitCost = (Σ component_value × QTY_SCALE) / producedQty
 *
 * `consumedValue` is in minor units of currency; `producedQty` is
 * scaled ×1000. Multiplied by QTY_SCALE to obtain cost PER WHOLE
 * UNIT (not per thousandth). Returns 0 if producedQty <= 0.
 */
export function producedUnitCost(consumedValue: number, producedQty: number): number {
  if (producedQty <= 0) {
    return 0;
  }
  return Math.round((consumedValue * QTY_SCALE) / producedQty);
}
